import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  FileUpload,
  Form,
  FormField,
  Input,
  Modal,
  Multiselect,
  SpaceBetween,
  Textarea,
} from "@amzn/awsui-components-react/polaris";

import {
  createDashboard,
  updateDashboard,
  deleteDashboard,
  deleteDashboardPreferences,
  fetchAllApps,
  uploadLogo,
  getLogoViewUrl,
} from "./api";
import { createAuditLog } from "../Templates/Utils/AppActivityUtils";
import { selectIsEffectiveAdmin, selectIsEffectiveSupportAdmin } from "../../utilities/adminUtils";

/**
 * Create / Edit modal for a Task Manager dashboard.
 *
 * When `dashboard` is provided the modal runs in edit mode: fields are
 * prepopulated from it, the primary action saves via updateDashboard, and the
 * parent is notified through onSaved. Otherwise it creates a new dashboard and
 * notifies through onCreated.
 *
 * Editing is a privileged action; the page only opens this in edit mode for
 * portal / support admins.
 *
 * Deleting is gated again here, on the same portal / support admin roles the
 * deleteDashboard API enforces server-side. The callers already restrict who can
 * open edit mode, so this is defence in depth: it keeps the destructive action
 * from ever appearing if this modal is later opened from a looser surface.
 */
const CreateTaskManagerModal = ({ visible, onDismiss, onCreated, onSaved, onDeleted, dashboard }) => {
  const isEdit = Boolean(dashboard && dashboard.dashboard_id);

  const userId = useSelector((globalState) => globalState.auth.userId);
  const isPortalAdmin = useSelector(selectIsEffectiveAdmin);
  const isSupportAdmin = useSelector(selectIsEffectiveSupportAdmin);
  const canDelete = isEdit && (isPortalAdmin || isSupportAdmin);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedApps, setSelectedApps] = useState([]);
  const [appOptions, setAppOptions] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [logoFiles, setLogoFiles] = useState([]);
  // Viewable URL for the logo already stored on the dashboard, so edit mode can
  // show what is set today instead of looking like the logo is missing.
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Delete-dashboard confirmation. Kept separate from `submitting` so the
  // destructive flow has its own spinner and cannot be triggered mid-save.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load the apps for the multiselect when the modal opens.
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setAppsLoading(true);
    fetchAllApps()
      .then((apps) => {
        if (cancelled) return;
        setAppOptions(
          (apps || []).map((app) => ({
            label: app.app_name,
            value: String(app.app_id),
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setAppOptions([]);
      })
      .finally(() => {
        if (!cancelled) setAppsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  // Prepopulate (edit) or clear (create) the form whenever the modal opens.
  useEffect(() => {
    if (!visible) return;
    if (isEdit) {
      setName(dashboard.name || "");
      setDescription(dashboard.description || "");
    } else {
      setName("");
      setDescription("");
      setSelectedApps([]);
    }
    setLogoFiles([]);
    setError("");
    setSubmitting(false);
    // Clear the destructive flow too. reset() only runs when the modal is closed via
    // handleDismiss or a delete outcome; if the parent ever closes it another way,
    // confirmDeleteOpen would still be true and reopening Edit would land straight on
    // the delete confirmation.
    setConfirmDeleteOpen(false);
    setDeleting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, dashboard]);

  // Resolve the dashboard's stored logo object key to a short-lived viewable URL
  // so the current logo can be previewed under the upload control. Best-effort:
  // if Vault cannot resolve it we simply show no preview.
  useEffect(() => {
    if (!visible || !isEdit || !dashboard.logo) {
      setCurrentLogoUrl(null);
      return;
    }
    let cancelled = false;
    getLogoViewUrl(dashboard.logo)
      .then((url) => {
        if (!cancelled) setCurrentLogoUrl(url || null);
      })
      .catch(() => {
        if (!cancelled) setCurrentLogoUrl(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isEdit, dashboard?.logo]);

  // Once the app options are loaded in edit mode, map the dashboard's app_ids
  // to selected options (so tokens show the app names).
  useEffect(() => {
    if (!visible || !isEdit || appOptions.length === 0) return;
    const ids = (dashboard.app_ids || []).map((id) => String(id));
    setSelectedApps(appOptions.filter((o) => ids.includes(o.value)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isEdit, appOptions, dashboard]);

  const reset = () => {
    setName("");
    setDescription("");
    setSelectedApps([]);
    setLogoFiles([]);
    setCurrentLogoUrl(null);
    setError("");
    setSubmitting(false);
    setConfirmDeleteOpen(false);
    setDeleting(false);
  };

  const handleDismiss = () => {
    if (submitting || deleting) return;
    reset();
    onDismiss();
  };

  // Delete the dashboard and everything attached to its dashboard_id. The RDS
  // cascade runs server-side; the DynamoDB column-config / status-mapping
  // preferences are cleaned up separately (best-effort — a failure there does
  // not fail the delete, since the dashboard is already gone from RDS).
  const handleDelete = async () => {
    if (!canDelete) return;
    setError("");
    setDeleting(true);
    try {
      const beforeState = dashboard;
      createAuditLog(
        `TASK_MANAGER#${dashboard.dashboard_id}`,
        `DELETE_REQ#${Date.now()}`,
        "Task Manager delete requested",
        JSON.stringify(beforeState),
        JSON.stringify({ dashboard_id: dashboard.dashboard_id }),
        "TASK MANAGER DELETE REQ",
        userId
      );

      const result = await deleteDashboard(dashboard.dashboard_id);
      if (result && result.error) {
        // Close the confirmation modal, otherwise it stays mounted on top and hides the
        // outer form's errorText, leaving the user with no feedback on the failure.
        setConfirmDeleteOpen(false);
        setError(result.error);
        setDeleting(false);
        return;
      }

      // Clean up the dashboard's DynamoDB preferences (column config + status
      // mapping). Best-effort: log but do not block, the RDS delete succeeded.
      try {
        const { errors } = await deleteDashboardPreferences(dashboard.dashboard_id);
        if (errors && errors.length > 0) {
          console.warn("[TaskManager] preference cleanup issues on delete:", errors);
        }
      } catch (prefErr) {
        console.warn("[TaskManager] preference cleanup failed on delete:", prefErr);
      }

      createAuditLog(
        `TASK_MANAGER#${dashboard.dashboard_id}`,
        `DELETE#${Date.now()}`,
        "Task Manager deleted",
        JSON.stringify(beforeState),
        JSON.stringify(result),
        "TASK MANAGER DELETED",
        userId
      );

      setConfirmDeleteOpen(false);
      if (onDeleted) onDeleted(dashboard.dashboard_id);
      reset();
      onDismiss();
    } catch (e) {
      setConfirmDeleteOpen(false);
      setError("Failed to delete the dashboard. Please try again.");
      setDeleting(false);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      // Upload the logo (if a new one was chosen) through Vault first; persist
      // the returned S3 object key. In edit mode, keep the existing logo when
      // no new file is chosen.
      let logo = isEdit ? dashboard.logo || "" : "";
      if (logoFiles.length > 0) {
        try {
          logo = await uploadLogo(logoFiles[0]);
        } catch (uploadErr) {
          setError(uploadErr?.message || "Logo upload failed");
          setSubmitting(false);
          return;
        }
      }

      const payload = {
        name: name.trim(),
        description: description.trim(),
        logo,
        app_ids: selectedApps.map((o) => Number(o.value)),
      };

      // Audit "before": snapshot the prior state (the existing dashboard on
      // edit, empty on create) and the requested change, before applying it.
      const auditPk = `TASK_MANAGER#${isEdit ? dashboard.dashboard_id : "NEW"}`;
      const beforeState = isEdit ? dashboard : {};
      createAuditLog(
        auditPk,
        `${isEdit ? "UPDATE" : "CREATE"}_REQ#${Date.now()}`,
        `Task Manager ${isEdit ? "update" : "create"} requested`,
        JSON.stringify(beforeState),
        JSON.stringify(payload),
        isEdit ? "TASK MANAGER UPDATE REQ" : "TASK MANAGER CREATE REQ",
        userId
      );

      let result;
      if (isEdit) {
        result = await updateDashboard({ dashboard_id: dashboard.dashboard_id, ...payload });
      } else {
        result = await createDashboard(payload);
      }

      if (result && result.error) {
        setError(result.error);
        setSubmitting(false);
        return;
      }

      // Audit "after": record the applied result once the change succeeded.
      createAuditLog(
        `TASK_MANAGER#${result?.dashboard_id ?? (isEdit ? dashboard.dashboard_id : "NEW")}`,
        `${isEdit ? "UPDATE" : "CREATE"}#${Date.now()}`,
        `Task Manager ${isEdit ? "updated" : "created"}`,
        JSON.stringify(beforeState),
        JSON.stringify(result),
        isEdit ? "TASK MANAGER UPDATED" : "TASK MANAGER CREATED",
        userId
      );

      if (isEdit) {
        if (onSaved) onSaved(result);
      } else if (onCreated) {
        onCreated(result);
      }
      reset();
      onDismiss();
    } catch (e) {
      setError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={isEdit ? "Edit Task Manager" : "Create Task Manager"}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {canDelete && (
              // Destructive delete lives on the left of the footer, visually
              // separated from the primary Save action. Portal/support admins
              // only, matching the deleteDashboard API's own check.
              <Button
                variant="normal"
                iconName="remove"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={submitting || deleting}
              >
                Delete
              </Button>
            )}
            <Button variant="link" onClick={handleDismiss} disabled={submitting || deleting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!name.trim() || deleting}
            >
              {isEdit ? "Save changes" : "Create"}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form errorText={error}>
        <SpaceBetween direction="vertical" size="l">
          <FormField label="Name">
            <Input
              value={name}
              placeholder="Enter a name"
              onChange={({ detail }) => setName(detail.value)}
            />
          </FormField>
          <FormField label="Description" description="Optional">
            <Textarea
              value={description}
              placeholder="What is this task manager for?"
              onChange={({ detail }) => setDescription(detail.value)}
            />
          </FormField>
          <FormField label="Include Apps">
            <Multiselect
              selectedOptions={selectedApps}
              options={appOptions}
              onChange={({ detail }) => setSelectedApps(detail.selectedOptions)}
              filteringType="auto"
              statusType={appsLoading ? "loading" : "finished"}
              loadingText="Loading apps"
              placeholder="Select apps to include..."
              tokenLimit={5}
              empty="No apps available"
            />
          </FormField>
          <FormField
            label="Logo"
            description={
              isEdit
                ? "Optional. The current logo is kept unless you upload a replacement."
                : "Optional. PNG, JPG, SVG or GIF."
            }
          >
            <SpaceBetween direction="vertical" size="xs">
              <FileUpload
                value={logoFiles}
                onChange={({ detail }) => setLogoFiles(detail.value)}
                accept="image/png,image/jpeg,image/svg+xml,image/gif"
                showFileLastModified
                showFileSize
                constraintText="Upload a single image to use as the task manager logo."
                i18nStrings={{
                  uploadButtonText: (multiple) => (multiple ? "Choose files" : "Choose file"),
                  dropzoneText: (multiple) => (multiple ? "Drop files to upload" : "Drop file to upload"),
                  removeFileAriaLabel: (index) => `Remove file ${index + 1}`,
                  limitShowFewer: "Show fewer files",
                  limitShowMore: "Show more files",
                  errorIconAriaLabel: "Error",
                }}
              />
              {/* Show what is already set so edit mode does not read as "no logo".
                  Only in edit mode; a create has nothing to preview. */}
              {isEdit && (
                <Box>
                  <Box variant="awsui-key-label">Current logo</Box>
                  {currentLogoUrl ? (
                    <SpaceBetween direction="horizontal" size="s" alignItems="center">
                      <img
                        src={currentLogoUrl}
                        alt={`${dashboard.name || "Task manager"} logo`}
                        style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 6 }}
                      />
                      {logoFiles.length > 0 && (
                        <Box variant="small" color="text-status-info">
                          Will be replaced by the file above on save.
                        </Box>
                      )}
                    </SpaceBetween>
                  ) : (
                    <Box variant="small" color="text-body-secondary">
                      {dashboard.logo ? "Could not load the current logo." : "No logo set."}
                    </Box>
                  )}
                </Box>
              )}
            </SpaceBetween>
          </FormField>
        </SpaceBetween>
      </Form>

      {canDelete && (
        <Modal
          visible={confirmDeleteOpen}
          onDismiss={() => {
            if (deleting) return;
            setConfirmDeleteOpen(false);
          }}
          header="Delete task manager"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  onClick={() => setConfirmDeleteOpen(false)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleDelete} loading={deleting}>
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween direction="vertical" size="s">
            <Box variant="span">
              This permanently deletes <b>{dashboard?.name || "this task manager"}</b> and
              everything attached to it — its deliverables and tracking history, custom columns,
              settings, owners and viewers, and any adhoc tasks. This cannot be undone.
            </Box>
          </SpaceBetween>
        </Modal>
      )}
    </Modal>
  );
};

export default CreateTaskManagerModal;
